import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { BarChart3, HandCoins, Images, LogOut, MessagesSquare, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLUB_OPTIONS = ["events", "lekka", "wellbeing", "reading", "writing", "debate", "film"];
const CLUB_LABELS = {
  events: "Events",
  lekka: "Workshops (English is Lekka)",
  wellbeing: "Wellness",
  reading: "Reading Club",
  writing: "Writing Club",
  debate: "Debate Club",
  film: "Film & Media Club",
};
const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "photos", label: "Photos", icon: Images },
  { id: "comments", label: "Comments", icon: MessagesSquare },
  { id: "donations", label: "Donations", icon: HandCoins },
];

const auth = { withCredentials: true };

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/admin/stats`, auth).then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === null) {
    return (
      <div className="bg-sand py-32 text-center" data-testid="admin-loading">
        <p className="text-sm text-coal-muted">Checking your session...</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="bg-sand grain py-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.25em] text-ember">Admin</p>
            <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
              Site dashboard
            </h1>
            <p className="mt-1 text-sm text-coal-muted">Signed in as {user.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-coal/15 px-5 py-2.5 font-heading text-sm font-bold text-coal transition-colors hover:border-ember hover:text-ember"
          >
            <LogOut size={15} />
            Log out
          </button>
        </div>

        <div className="mt-8 flex gap-2" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-heading text-sm font-bold transition-colors ${
                tab === t.id ? "bg-coal text-sand" : "bg-white text-coal hover:bg-teal-subtle"
              }`}
            >
              <t.icon size={15} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && <Overview stats={stats} />}
          {tab === "photos" && <Photos onChanged={() => axios.get(`${API}/admin/stats`, auth).then((r) => setStats(r.data))} />}
          {tab === "comments" && <Comments onChanged={() => axios.get(`${API}/admin/stats`, auth).then((r) => setStats(r.data))} />}
          {tab === "donations" && <Donations />}
        </div>
      </div>
    </div>
  );
}

const Overview = ({ stats }) => {
  if (!stats) return <p className="text-sm text-coal-muted">Loading stats...</p>;
  return (
    <div data-testid="admin-overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Gallery photos", value: stats.items, testId: "stat-total-photos" },
          { label: "Total likes", value: stats.totals.likes, testId: "stat-total-likes" },
          { label: "Total comments", value: stats.totals.comments, testId: "stat-total-comments" },
          { label: "Total shares", value: stats.totals.shares, testId: "stat-total-shares" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-coal/5 bg-white p-6" data-testid={s.testId}>
            <p className="font-display text-4xl font-extrabold text-teal">{s.value}</p>
            <p className="mt-1 text-sm text-coal-muted">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 overflow-hidden rounded-2xl border border-coal/5 bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-coal/5 bg-sand font-heading text-xs uppercase tracking-wider text-coal-muted">
              <th className="px-5 py-3">Section</th>
              <th className="px-5 py-3">Photos</th>
              <th className="px-5 py-3">Likes</th>
              <th className="px-5 py-3">Comments</th>
              <th className="px-5 py-3">Shares</th>
            </tr>
          </thead>
          <tbody>
            {stats.clubs.map((c) => (
              <tr key={c.club} className="border-b border-coal/5 last:border-0" data-testid={`club-row-${c.club}`}>
                <td className="px-5 py-3 font-semibold text-coal">{CLUB_LABELS[c.club] || c.club}</td>
                <td className="px-5 py-3 text-coal-muted">{c.count}</td>
                <td className="px-5 py-3 text-coal-muted">{c.likes}</td>
                <td className="px-5 py-3 text-coal-muted">{c.comments}</td>
                <td className="px-5 py-3 text-coal-muted">{c.shares}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Photos = ({ onChanged }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ club: "events", year: "2026", title: "", caption: "", image: "", fit: "cover" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => axios.get(`${API}/gallery`, auth).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/admin/gallery`, form, auth);
      toast.success("Photo added");
      setForm({ ...form, title: "", caption: "", image: "" });
      await load();
      onChanged();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/gallery/${item.id}`, auth);
      toast.success("Photo deleted");
      await load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const field = "w-full rounded-xl border border-coal/15 bg-sand px-4 py-2.5 text-sm text-coal outline-none transition-colors focus:border-teal";

  return (
    <div data-testid="admin-photos">
      <form onSubmit={add} className="rounded-2xl border border-coal/5 bg-white p-6">
        <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-coal">
          <Plus size={18} className="text-ember" /> Add a photo
        </h2>
        {error && <p className="mt-3 rounded-xl bg-ember-subtle px-4 py-2.5 text-sm font-semibold text-ember" data-testid="photo-add-error">{error}</p>}
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="photo-club" className="mb-1 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">Section</label>
            <select id="photo-club" data-testid="photo-club-select" value={form.club} onChange={(e) => setForm({ ...form, club: e.target.value })} className={field}>
              {CLUB_OPTIONS.map((c) => <option key={c} value={c}>{CLUB_LABELS[c]}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="photo-year" className="mb-1 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">Year</label>
            <input id="photo-year" data-testid="photo-year-input" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={field} pattern="\d{4}" required />
          </div>
          <div>
            <label htmlFor="photo-fit" className="mb-1 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">Fit</label>
            <select id="photo-fit" data-testid="photo-fit-select" value={form.fit} onChange={(e) => setForm({ ...form, fit: e.target.value })} className={field}>
              <option value="cover">Cover (fill the card)</option>
              <option value="contain">Contain (show whole image)</option>
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label htmlFor="photo-title" className="mb-1 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">Title</label>
            <input id="photo-title" data-testid="photo-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} required maxLength={120} />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label htmlFor="photo-caption" className="mb-1 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">Caption</label>
            <input id="photo-caption" data-testid="photo-caption-input" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className={field} required maxLength={500} />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label htmlFor="photo-image" className="mb-1 block font-heading text-xs font-bold uppercase tracking-wider text-coal-muted">Image URL</label>
            <input id="photo-image" data-testid="photo-image-input" type="url" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={field} required placeholder="https://..." />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="photo-add-button" className="mt-5 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-2.5 font-heading text-sm font-bold text-sand transition-colors hover:bg-teal disabled:opacity-60">
          <Plus size={15} /> {busy ? "Adding..." : "Add photo"}
        </button>
      </form>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items === null ? (
          <p className="text-sm text-coal-muted">Loading photos...</p>
        ) : (
          items.map((item) => (
            <div key={item.id} className="group relative overflow-hidden rounded-2xl border border-coal/5 bg-white" data-testid={`admin-photo-${item.id}`}>
              <img src={item.image} alt={item.title} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <div className="p-3">
                <p className="truncate font-heading text-sm font-bold text-coal">{item.title}</p>
                <p className="text-xs text-coal-muted">{CLUB_LABELS[item.club] || item.club} · {item.year} · {item.likes} likes</p>
              </div>
              <button
                onClick={() => remove(item)}
                data-testid={`photo-delete-${item.id}`}
                aria-label={`Delete ${item.title}`}
                className="absolute right-2 top-2 rounded-full bg-coal-dark/70 p-2 text-sand backdrop-blur transition-opacity hover:bg-ember md:opacity-0 md:group-hover:opacity-100"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const Comments = ({ onChanged }) => {
  const [comments, setComments] = useState(null);

  const load = () => axios.get(`${API}/admin/comments`, auth).then((r) => setComments(r.data)).catch(() => setComments([]));
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete this comment by ${c.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/gallery/${c.item_id}/comments/${c.id}`, auth);
      toast.success("Comment deleted");
      await load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-comments">
      {comments === null ? (
        <p className="text-sm text-coal-muted">Loading comments...</p>
      ) : comments.length === 0 ? (
        <div className="rounded-2xl border border-coal/5 bg-white p-10 text-center" data-testid="comments-empty">
          <MessagesSquare size={28} className="mx-auto text-coal-muted" />
          <p className="mt-3 text-sm text-coal-muted">No comments yet. When visitors comment on gallery photos, they will appear here for moderation.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start justify-between gap-4 rounded-2xl border border-coal/5 bg-white p-5" data-testid={`comment-row-${c.id}`}>
              <div>
                <p className="text-xs text-coal-muted">
                  <span className="font-bold text-teal">{c.name}</span> on{" "}
                  <span className="font-semibold text-coal">{c.item_title}</span> · {CLUB_LABELS[c.club] || c.club} {c.year}
                </p>
                <p className="mt-1.5 text-sm text-coal">{c.text}</p>
                <p className="mt-1 text-[11px] text-coal-muted">{new Date(c.created_at).toLocaleString()}</p>
              </div>
              <button
                onClick={() => remove(c)}
                data-testid={`comment-delete-${c.id}`}
                aria-label="Delete comment"
                className="shrink-0 rounded-full border border-coal/10 p-2.5 text-coal-muted transition-colors hover:border-ember hover:text-ember"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TIER_LABELS = {
  transport: "Transport for a learner",
  books: "Books and stationery",
  tablets: "Tablets and resources",
  learner_year: "Sponsor a learner for a year",
  custom: "Custom amount",
};

const Donations = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/donations`, auth).then((r) => setData(r.data)).catch(() => setData({ donations: [], total_paid_zar: "0" }));
  }, []);

  if (!data) return <p className="text-sm text-coal-muted">Loading donations...</p>;

  const markPaid = async (d) => {
    if (!window.confirm(`Mark this R${Number(d.amount).toLocaleString("en-ZA")} donation as PAID? Only do this when you have proof of payment from PayFast.`)) return;
    try {
      await axios.post(`${API}/admin/donations/${d.id}/mark-paid`, {}, auth);
      toast.success("Donation marked as paid");
      axios.get(`${API}/admin/donations`, auth).then((r) => setData(r.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const badge = (status) =>
    status === "PAID"
      ? "bg-teal-subtle text-teal"
      : status === "PENDING"
        ? "bg-gold/20 text-coal"
        : "bg-ember-subtle text-ember";

  return (
    <div data-testid="admin-donations">
      <div className="rounded-2xl border border-coal/5 bg-white p-6">
        <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.25em] text-ember">Total received</p>
        <p className="mt-1 font-display text-4xl font-extrabold text-teal" data-testid="donations-total">
          R{Number(data.total_paid_zar).toLocaleString("en-ZA")}
        </p>
      </div>
      {data.donations.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-coal/5 bg-white p-10 text-center" data-testid="donations-empty">
          <HandCoins size={28} className="mx-auto text-coal-muted" />
          <p className="mt-3 text-sm text-coal-muted">No donations yet. When visitors donate, they will appear here.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-coal/5 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-coal/5 bg-sand font-heading text-xs uppercase tracking-wider text-coal-muted">
                <th className="px-5 py-3">Tier</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">PayFast ref</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.donations.map((d) => (
                <tr key={d.id} className="border-b border-coal/5 last:border-0" data-testid={`donation-row-${d.id}`}>
                  <td className="px-5 py-3 font-semibold text-coal">{TIER_LABELS[d.tier_id] || d.tier_id}</td>
                  <td className="px-5 py-3 text-coal-muted">R{Number(d.amount).toLocaleString("en-ZA")}</td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full px-3 py-1 font-heading text-xs font-bold ${badge(d.status)}`} data-testid={`donation-status-${d.id}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono-brand text-xs text-coal-muted">{d.pf_payment_id || "—"}</td>
                  <td className="px-5 py-3 text-coal-muted">{new Date(d.created_at).toLocaleString()}</td>
                  <td className="px-5 py-3">
                    {d.status === "PENDING" && (
                      <button
                        onClick={() => markPaid(d)}
                        data-testid={`donation-mark-paid-${d.id}`}
                        className="rounded-full border border-teal/30 px-3.5 py-1.5 font-heading text-xs font-bold text-teal transition-colors hover:bg-teal hover:text-sand"
                      >
                        Mark paid
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
